import type { ConnectionItemProps } from "@wayflyer/flyui";
import { ConnectionItem, Flex, Image, Text } from "@wayflyer/flyui";
import { IconShop24Line } from "@wayflyer/flyui-icons/24/line";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { connectByPath, CODAT_PLATFORM_LOGOS } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { AuthCallbackPath } from "../../../api-types/connector-callbacks";
import { usePlatformConnectorProperties } from "../../../hooks/connector";
import { useFundingApplication } from "../../../queries/funding-application/queries";
import { COMMON_MESSAGES } from "../../common-messages";
import { OAuthErrorBanner } from "../common/oauth-error-banner";
import { MESSAGES } from "../message-definitions";

const CONNECTOR_SLUG = "codat_accounting";

export const CodatConnect = ({ onInitiateConnection }: { onInitiateConnection?: () => void }) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const [connectionInProgress, setConnectionInProgress] = useState(false);
  const [searchParams] = useSearchParams();
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    AuthCallbackPath.FUNDING_APPLICATION_ADD_CONNECTION
  );

  // Prefer the platform name passed via URL param (new picker flow), then fall back to
  // selectedUnsupportedAccounting (legacy flow), then the generic connector name.
  const legacyPlatformName = fundingApplication.selectedUnsupportedAccounting.join(", ");
  const selectedPlatformName = searchParams.get("codat_platform") ?? (legacyPlatformName || connectorProperties.name);

  const getPlatformLogo = (name: string) => {
    const logoSrc = CODAT_PLATFORM_LOGOS[name];
    return logoSrc ? <Image src={logoSrc} /> : <IconShop24Line />;
  };

  const connectionItems: ConnectionItemProps[] = connectorProperties.connections.length
    ? connectorProperties.connections.map((connection, index) => {
        const label = connection.identifierName ?? connectorProperties.name;
        return {
          testId: `${connection.id}-${index}`,
          label,
          logo: getPlatformLogo(label),
          badgeStatus: "positive",
          badgeText: intl.formatMessage(COMMON_MESSAGES.connected),
        };
      })
    : [];

  return (
    <Flex direction="column" gap="8">
      <Flex direction="column" gap="8" align="flex-start" justify="space-between">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.accountingPlatformDataUsage)}</Text>
        {connectorProperties ? (
          connectionItems.length ? (
            <Flex direction="column" gap="2" width="100%">
              {connectionItems.map((item, idx) => (
                <ConnectionItem key={idx} {...item} />
              ))}
            </Flex>
          ) : (
            <Flex direction="column" width="100%">
              <ConnectionItem
                logo={getPlatformLogo(selectedPlatformName)}
                label={selectedPlatformName}
                actions={[
                  {
                    content: intl.formatMessage(COMMON_MESSAGES.connectAccount),
                    variant: "Tertiary",
                    onAction: () => {
                      onInitiateConnection?.();
                      setConnectionInProgress(true);
                      connectByPath(connectorProperties?.connectPath);
                    },
                    loading: connectionInProgress,
                  },
                ]}
              />
            </Flex>
          )
        ) : null}
      </Flex>
    </Flex>
  );
};
